import { useState } from "react";
import { Mail, ExternalLink } from "lucide-react";
import { STATE_ICON } from "./ReadinessCard";

const field = "w-full text-xs rounded-lg border border-stone-200 px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-300";
const pill = (active) => `text-xs font-semibold px-3 py-1.5 rounded-full transition ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`;

export function ItemRow({ item, when, onSave }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(item.note || "");
  const [link, setLink] = useState(item.link || "");
  const manual = !!onSave;
  const sub = manual
    ? (item.done ? `Ticked by ${item.by} · ${when(item.at)}${item.note ? ` · ${item.note}` : ""}` : item.state === "not_needed" ? "Not needed while AI for people you support is off" : item.required ? "Not yet" : "Not yet · recommended")
    : item.how;
  return (
    <li className="py-2.5" data-testid={`readiness-item-${item.key}`} data-state={item.state}>
      <div className="flex items-start gap-3">
        {manual ? (
          <button type="button" aria-label={item.done ? "Un-tick" : "Tick"} data-testid={`readiness-tick-${item.key}`} className="mt-0.5"
            onClick={() => (item.done ? onSave({ done: false, note, link }) : setOpen((o) => !o))}>{STATE_ICON[item.state]}</button>
        ) : <span className="mt-0.5">{STATE_ICON[item.state]}</span>}
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${item.state === "not_needed" ? "text-stone-400" : "text-stone-800"}`}>{item.title}</div>
          <div className="text-xs text-stone-500 mt-0.5 break-words" data-testid={`readiness-how-${item.key}`}>{sub}
            {item.link && <a href={item.link} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 text-rose-800 hover:underline">link <ExternalLink className="w-3 h-3" /></a>}
          </div>
          {open && !item.done && (
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]" data-testid={`readiness-form-${item.key}`}>
              <input className={field} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`readiness-note-${item.key}`} />
              <input className={field} placeholder="Link to the document (optional)" value={link} onChange={(e) => setLink(e.target.value)} data-testid={`readiness-link-${item.key}`} />
              <button type="button" className={pill(true)} data-testid={`readiness-save-${item.key}`} onClick={() => { onSave({ done: true, note, link }); setOpen(false); }}>Tick</button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export function HostingPicker({ hosting, options, baaReply, onPick }) {
  const hint = baaReply?.outcome === "available" ? "Emergent says a BAA is available — Emergent hosting becomes an option once it is signed."
    : baaReply && baaReply.outcome !== "none" ? "Emergent has no BAA for now — self-hosting on a BAA-covered cloud is the path." : null;
  return (
    <div className="rounded-xl bg-stone-50 p-3" data-testid="readiness-hosting">
      <div className="text-xs font-semibold text-stone-700 mb-1.5">Hosting decision</div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(options).map(([k, label]) => <button key={k} type="button" className={pill(hosting.choice === k)} data-testid={`hosting-${k}`} onClick={() => onPick(k)}>{label}</button>)}
      </div>
      <div className="text-[11px] text-stone-500 mt-1.5">{hosting.at ? `Set by ${hosting.by} · ${new Date(hosting.at).toLocaleDateString()}` : "Not decided yet."}{hint ? ` ${hint}` : ""}</div>
    </div>
  );
}

export function BaaSection({ baa, outcomes, mailto, when, onSent, onClear, onReply }) {
  const [open, setOpen] = useState(false);
  const r = baa.reply;
  const [form, setForm] = useState({ outcome: r?.outcome || "none", date: r?.date || "", text: r?.text || "", link: r?.link || "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="rounded-xl bg-stone-50 p-3 space-y-2" data-testid="readiness-baa">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs font-semibold text-stone-700">BAA request to Emergent</div>
          <div className="text-[11px] text-stone-500" data-testid="baa-status">{baa.sent_at ? `Sent · ${when(baa.sent_at)} · ${baa.sent_by}` : "Not sent yet — opens in your own mail app so the reply lands in your inbox."}</div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <a href={mailto} onClick={onSent} data-testid="baa-send" className={`inline-flex items-center gap-1.5 ${pill(!baa.sent_at)}`}><Mail className="w-3.5 h-3.5" /> {baa.sent_at ? "Send again" : "Send BAA request"}</a>
          {baa.sent_at && <button type="button" className={pill(false)} data-testid="baa-clear" onClick={onClear}>Clear</button>}
          <button type="button" className={pill(false)} data-testid="baa-reply-toggle" onClick={() => setOpen((o) => !o)}>{r ? "Edit reply" : "Record reply"}</button>
        </div>
      </div>
      {r && !open && <div className="text-xs text-stone-600" data-testid="baa-reply">Reply: <span className="font-semibold">{outcomes[r.outcome]}</span>{r.date ? ` · ${r.date}` : ""}{r.link ? <a href={r.link} target="_blank" rel="noreferrer" className="ml-1 text-rose-800 hover:underline">link</a> : ""}{r.text ? <div className="text-stone-500 mt-0.5 whitespace-pre-wrap line-clamp-3">{r.text}</div> : null}</div>}
      {open && (
        <div className="grid gap-2 sm:grid-cols-2" data-testid="baa-reply-form">
          <select className={field} value={form.outcome} onChange={set("outcome")} data-testid="baa-outcome">{Object.entries(outcomes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <input className={field} type="date" value={form.date} onChange={set("date")} data-testid="baa-date" />
          <textarea className={`${field} sm:col-span-2`} rows={3} placeholder="Paste the reply (optional)" value={form.text} onChange={set("text")} data-testid="baa-text" />
          <input className={field} placeholder="Link (optional)" value={form.link} onChange={set("link")} data-testid="baa-link" />
          <button type="button" className={pill(true)} data-testid="baa-reply-save" onClick={() => { onReply({ ...form, date: form.date || null }); setOpen(false); }}>Save reply</button>
        </div>
      )}
    </div>
  );
}

export function AiSwitchCard({ sw, onToggle }) {
  const missing = [!sw.provider && "a PHI-lane provider configured with the practice's own key", !sw.provider_baa_signed && "the AI-provider BAA ticked above"].filter(Boolean);
  return (
    <div className="rounded-xl border border-stone-200 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="ai-switch-card">
      <div className="max-w-xl">
        <div className="text-sm font-semibold text-stone-800">AI with member data: <span data-testid="ai-switch-state">{sw.enabled ? "On" : "Off"}</span></div>
        <p className="text-xs text-stone-500 mt-1">
          {sw.enabled ? `Linked clients' de-identified prompts go to the PHI lane (${sw.provider}); every output is held for clinician review.`
            : "Off means no model ever sees a linked client's data; solo clients and sample gardeners keep the general lane, which never carries PHI."}
          {!sw.enabled && missing.length > 0 && <span className="block mt-1 text-stone-400">Turning it on needs {missing.join(" and ")}.</span>}
        </p>
        <p className="text-[11px] text-stone-400 mt-1">PHI-lane slot: {sw.provider || "none"} · future: {sw.future_providers.join(", ")} (named, not wired)</p>
      </div>
      <button type="button" role="switch" aria-checked={sw.enabled} data-testid="ai-switch-toggle" disabled={!sw.enabled && !sw.can_enable}
        onClick={() => onToggle(!sw.enabled)}
        className={`relative h-7 w-12 rounded-full transition ${sw.enabled ? "bg-emerald-600" : "bg-stone-300"} disabled:opacity-50`}>
        <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${sw.enabled ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}
